import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model.ts';
import type { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model.ts';
import { SpeciesV1 } from '@/model/Species.model.ts';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import { _notNil } from '@/littledash.ts';
import { errorToast, renderMetadataValue, successToast } from '@/helpers.tsx';
import { MetadataField } from '@/model/Metadata.model.ts';
import { modalAction } from '@/utils/modal';
import type { Study } from '@/model/Study.model';
import type { Dispatch } from 'redux';
import { ApiService } from '@/support/ApiService.ts';
import { AnimalApiId, AnimalSex, AnimalSexMap } from '@/model/Animal.model.ts';
import Link from '@/components/UI/Link';
import { web as webRoute } from '@/support/route.ts';
import { ID } from '@/model/Common.model.ts';
import { AnimalDefinitionApiId, AnimalDefinitionTargetAnimal } from '@/model/PlannedAnimal.model.ts';
import { FilterTypes as filterType } from '@/constants/FilterTypes';

export interface PlannedAnimalDefinition {
  api_id: AnimalDefinitionApiId;
  animal_api_id: AnimalApiId | null;
  study_group_api_id: string;
  planned_id: string;
  sex: AnimalSex;
  species_api_id: string | null;
  strain_api_id: string | null;
  metadata: Array<{
    type: string;
    value: string;
  }>;
  created_at: string;
  updated_at: string;
}

export interface PlannedAnimalWithGroup extends PlannedAnimalDefinition {
  groupName?: string;
  study_group?: TreatmentGroup;
  species_name?: string;
  strain_name?: string;
  animal?: AnimalDefinitionTargetAnimal;
}

export const plannedAnimalColumns = (
  metadataOptions: Array<MetadataField>,
  studyId: ID
): Array<TanStackColumnDef<PlannedAnimalWithGroup>> => {
  const metadataColumns = getMetadataColumns(metadataOptions);
  return [
    {
      id: 'planned_id',
      header: 'Planned animal ID',
      accessorKey: 'planned_id',
      isVisible: true,
    },
    {
      id: 'group',
      header: 'Group',
      isVisible: true,
      cell: ({ row }: { row: { original: PlannedAnimalWithGroup } }) => {
        const studyGroup = row.original.study_group;
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return studyGroup ? <GroupLabel group={studyGroup} /> : null;
      },
    },
    {
      id: 'sex',
      header: 'Sex',
      accessorKey: 'sex',
      enableSorting: true,
      isVisible: true,
      cell: ({ row }: { row: { original: PlannedAnimalWithGroup } }) => {
        return AnimalSexMap[row.original.sex] ?? row.original.sex.toUpperCase();
      },
    },
    {
      id: 'mapped_animal',
      header: 'Mapped animal',
      isVisible: true,
      cell: ({ row }: { row: { original: PlannedAnimalWithGroup } }) => {
        const mappedAnimal = row.original.animal;
        return (
          <div className="flex justify-between items-center w-100">
            {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
            {mappedAnimal ? (
              <Link
                to={webRoute('animals.show', {
                  id: studyId,
                  subject_id: mappedAnimal.animal_id,
                })}
                className="link blue"
              >
                {mappedAnimal.name}
              </Link>
            ) : (
              <span className="text-muted">{'-'}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'species',
      header: 'Species',
      isVisible: true,
      accessorKey: 'species_name',
      enableSorting: false,
    },
    {
      id: 'strain',
      header: 'Strain',
      isVisible: true,
      accessorKey: 'strain_name',
      enableSorting: false,
    },
    ...metadataColumns,
  ];
};

const getMetadataColumns = (metadataOptions?: Array<MetadataField>) =>
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  metadataOptions?.map(({ id, title, glossary_api_id, field_type }) => ({
    id: String(id),
    header: title,
    isVisible: false,
    enableSorting: false,
    accessorFn: (row: PlannedAnimalWithGroup) => {
      const metadataItem = row.metadata.find((item) => item.type === glossary_api_id);
      return _notNil(metadataItem) ? renderMetadataValue({ value: metadataItem.value, field_type }) : '';
    },
  })) || [];

export const plannedAnimalBulkActions = (
  selectedFlatRows: Array<{ original: PlannedAnimalWithGroup }>,
  refetchTableData: () => Promise<void>,
  study: Study,
  dispatch: Dispatch
) => {
  const { openModal, closeModal } = modalAction(dispatch);

  const handleCallback = async (text: string) => {
    closeModal();
    await refetchTableData();
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (text) {
      successToast(text);
    }
  };

  const plannedAnimals = selectedFlatRows.map((row) => row.original);
  const handleDelete = async () => {
    try {
      await ApiService.call({
        endpoint: 'DELETE /api/v1/studies/{studyApiId}/planned/animals/remove',
        path: { studyApiId: study.api_id },
        body: { animal_definition_api_ids: plannedAnimals.map((animal) => animal.api_id) },
      });
      return handleCallback('Successfully deleted planned animals');
    } catch (error) {
      errorToast('There was a problem deleting planned animals, please try again later.');
    }
  };

  return [
    {
      name: 'Delete',
      action: () =>
        openModal('CONFIRM_DELETE_PLANNED_ANIMALS', {
          subject: plannedAnimals,
          closeModal,
          onClick: handleDelete,
        }),
      className: 'red',
    },
  ];
};

export const updateGroupCapacities = (
  animalsCreated: Array<AnimalDefinitionApiId>,
  plannedAnimalsData: Array<PlannedAnimalWithGroup>,
  groupsData: Array<TreatmentGroup>
): Array<TreatmentGroup> => {
  const groupCapacityUpdates: Map<GroupApiId, number> = animalsCreated.reduce<Map<GroupApiId, number>>(
    (acc, animalApiId) => {
      const animal = plannedAnimalsData.find((a) => a.api_id === animalApiId);
      if (_notNil(animal?.study_group_api_id)) {
        const currentCount = acc.get(animal.study_group_api_id as GroupApiId) ?? 0;
        acc.set(animal.study_group_api_id as GroupApiId, currentCount + 1);
      }
      return acc;
    },
    new Map()
  );
  return groupsData.map((group) => {
    const animalsAdded = groupCapacityUpdates.get(group.api_id as GroupApiId) ?? 0;
    if (animalsAdded > 0) {
      return {
        ...group,
        animals_count: (group.animals_count ?? 0) + animalsAdded,
      };
    }
    return group;
  });
};

export const plannedAnimalFilterOptions = (
  groups: Array<TreatmentGroup>,
  species: Array<SpeciesV1>
): Array<FilterOption> => {
  const groupOptions = groups.map((group) => ({
    name: group.name,
    value: group.api_id,
  }));
  const groupFilters: FilterOption = {
    value: 'study_group',
    name: 'Group',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: groupOptions,
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: groupOptions,
      },
    ],
  };

  const sexOptions = Object.entries(AnimalSexMap).map(([value, name]) => ({
    name,
    value,
  }));
  const sexFilter: FilterOption = {
    value: 'sex',
    name: 'Sex',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: sexOptions,
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: sexOptions,
      },
    ],
  };

  const speciesOptions = species.map((s) => ({
    name: s.name,
    value: s.api_id,
  }));
  const speciesFilter: FilterOption = {
    value: 'species',
    name: 'Species',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: speciesOptions,
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: speciesOptions,
      },
    ],
  };

  const strainOptions = species.reduce<Array<{ name: string; value: string }>>((acc, s) => {
    if (_notNil(s.strains)) {
      s.strains.forEach((strain) => {
        acc.push({
          name: strain.name,
          value: strain.api_id,
        });
      });
    }
    return acc;
  }, []);
  const strainFilter: FilterOption = {
    value: 'strain',
    name: 'Strain',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: strainOptions,
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: strainOptions,
      },
    ],
  };

  return [groupFilters, sexFilter, speciesFilter, strainFilter];
};
